/**
 * 모던 입력 컴포넌트
 * 다양한 타입과 상태를 지원하는 재사용 가능한 입력 필드
 */

import React, { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, spacing, borderRadius, shadows, animations, components } from '../../styles/designTokens';

// ===== 타입 정의 =====
export type InputVariant = 'default' | 'filled' | 'outlined' | 'underline';
export type InputSize = 'sm' | 'md' | 'lg';
export type InputState = 'default' | 'error' | 'success' | 'warning';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
    variant?: InputVariant;
    size?: InputSize;
    state?: InputState;
    label?: string;
    helperText?: string;
    errorText?: string;
    successText?: string;
    warningText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    leftAddon?: React.ReactNode;
    rightAddon?: React.ReactNode;
    fullWidth?: boolean;
}

// ===== 스타일드 컴포넌트 =====
const InputContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $fullWidth }) => $fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label<{ $state: InputState }>`
  display: block;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${({ $state }) => {
        switch ($state) {
            case 'error': return colors.error[600];
            case 'success': return colors.success[600];
            case 'warning': return colors.warning[600];
            default: return colors.semantic.text.primary;
        }
    }};
  margin-bottom: ${spacing[2]};
  transition: color ${animations.duration.fast} ${animations.easing.smooth};
`;

const InputWrapper = styled.div<{
    $variant: InputVariant;
    $size: InputSize;
    $state: InputState;
    $focused: boolean;
    $disabled: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: ${borderRadius.lg};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  
    /* 크기별 스타일 */
  ${({ $size }) => {
        switch ($size) {
            case 'sm':
                return css`
          height: ${components.input.height.sm};
          font-size: ${typography.fontSize.sm};
          
          @media (max-width: 480px) {
            height: 36px;
            font-size: ${typography.fontSize.xs};
          }
        `;
            case 'md':
                return css`
          height: ${components.input.height.md};
          font-size: ${typography.fontSize.sm};
          
          @media (max-width: 480px) {
            height: 40px;
            font-size: ${typography.fontSize.sm};
          }
        `;
            case 'lg':
                return css`
          height: ${components.input.height.lg};
          font-size: ${typography.fontSize.base};
          
          @media (max-width: 480px) {
            height: 44px;
            font-size: ${typography.fontSize.sm};
          }
        `;
        }
    }}
  
  /* Variant별 스타일 */
  ${({ $variant, $state, $focused, $disabled }) => {
        const getBorderColor = () => {
            if ($disabled) return colors.semantic.border.primary;
            if ($state === 'error') return colors.error[300];
            if ($state === 'success') return colors.success[300];
            if ($state === 'warning') return colors.warning[300];
            if ($focused) return colors.primary[500];
            return colors.semantic.border.primary;
        };

        const getBackgroundColor = () => {
            if ($disabled) return colors.semantic.background.tertiary;
            if ($variant === 'filled') return colors.semantic.background.tertiary;
            return colors.semantic.background.primary;
        };

        switch ($variant) {
            case 'default':
                return css`
          background-color: ${getBackgroundColor()};
          border: 1px solid ${getBorderColor()};
          box-shadow: ${$focused ? `0 0 0 3px ${colors.primary[100]}` : shadows.sm};
        `;

            case 'filled':
                return css`
          background-color: ${getBackgroundColor()};
          border: 1px solid transparent;
          box-shadow: ${$focused ? `0 0 0 3px ${colors.primary[100]}` : 'none'};
        `;

            case 'outlined':
                return css`
          background-color: transparent;
          border: 2px solid ${getBorderColor()};
          box-shadow: ${$focused ? `0 0 0 3px ${colors.primary[100]}` : 'none'};
        `;

            case 'underline':
                return css`
          background-color: transparent;
          border: none;
          border-bottom: 2px solid ${getBorderColor()};
          border-radius: 0;
          box-shadow: none;
        `;
        }
    }}
  
  /* 포커스 상태 */
  ${({ $focused, $state }) => $focused && css`
    transform: translateY(-1px);
    box-shadow: ${$state === 'error' ? `0 0 0 3px ${colors.error[100]}` :
            $state === 'success' ? `0 0 0 3px ${colors.success[100]}` :
                $state === 'warning' ? `0 0 0 3px ${colors.warning[100]}` :
                    `0 0 0 3px ${colors.primary[100]}`};
  `}
  
  /* 모션 감소 */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

const InputField = styled.input<{
    $size: InputSize;
    $hasLeftIcon: boolean;
    $hasRightIcon: boolean;
    $hasLeftAddon: boolean;
    $hasRightAddon: boolean;
}>`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: ${colors.semantic.text.primary};
  font-family: ${typography.fontFamily.sans};
  font-weight: ${typography.fontWeight.normal};
  
  /* 크기별 패딩 */
  ${({ $size, $hasLeftIcon, $hasRightIcon, $hasLeftAddon, $hasRightAddon }) => {
        const basePadding = $size === 'sm' ? '0.5rem' : $size === 'md' ? '0.75rem' : '1rem';
        const iconPadding = '2.5rem';
        const addonPadding = '3rem';

        let paddingLeft = basePadding;
        let paddingRight = basePadding;

        if ($hasLeftIcon || $hasLeftAddon) {
            paddingLeft = $hasLeftAddon ? addonPadding : iconPadding;
        }
        if ($hasRightIcon || $hasRightAddon) {
            paddingRight = $hasRightAddon ? addonPadding : iconPadding;
        }

        return css`
      padding: ${basePadding} ${paddingRight} ${basePadding} ${paddingLeft};
    `;
    }}
  
  &::placeholder {
    color: ${colors.semantic.text.tertiary};
    font-weight: ${typography.fontWeight.normal};
  }
  
  &:disabled {
    color: ${colors.semantic.text.disabled};
    cursor: not-allowed;
  }
  
  /* 자동완성 스타일 */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px ${colors.semantic.background.primary} inset;
    -webkit-text-fill-color: ${colors.semantic.text.primary};
    transition: background-color 5000s ease-in-out 0s;
  }
`;

const IconWrapper = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.tertiary};
  pointer-events: none;
  z-index: 1;
  
  ${({ $position }) => $position === 'left'
        ? css`left: ${spacing[3]};`
        : css`right: ${spacing[3]};`
    }
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const AddonWrapper = styled.div<{ $position: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.secondary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  background-color: ${colors.semantic.background.tertiary};
  border: 1px solid ${colors.semantic.border.primary};
  z-index: 1;
  
  ${({ $position }) => $position === 'left'
        ? css`
        left: 0;
        border-right: none;
        border-top-left-radius: ${borderRadius.lg};
        border-bottom-left-radius: ${borderRadius.lg};
        padding: 0 ${spacing[3]};
      `
        : css`
        right: 0;
        border-left: none;
        border-top-right-radius: ${borderRadius.lg};
        border-bottom-right-radius: ${borderRadius.lg};
        padding: 0 ${spacing[3]};
      `
    }
`;

const HelperText = styled.div<{ $state: InputState }>`
  margin-top: ${spacing[2]};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.normal};
  color: ${({ $state }) => {
        switch ($state) {
            case 'error': return colors.error[600];
            case 'success': return colors.success[600];
            case 'warning': return colors.warning[600];
            default: return colors.semantic.text.tertiary;
        }
    }};
  transition: color ${animations.duration.fast} ${animations.easing.smooth};
`;

// ===== 메인 컴포넌트 =====
export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            variant = 'default',
            size = 'md',
            state = 'default',
            label,
            helperText,
            errorText,
            successText,
            warningText,
            leftIcon,
            rightIcon,
            leftAddon,
            rightAddon,
            fullWidth = false,
            disabled = false,
            onFocus,
            onBlur,
            ...props
        },
        ref
    ) => {
        const [focused, setFocused] = useState(false);

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            onFocus?.(e);
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            onBlur?.(e);
        };

        // 상태별 텍스트 결정
        const getHelperText = () => {
            if (errorText) return errorText;
            if (successText) return successText;
            if (warningText) return warningText;
            return helperText;
        };

        const getState = (): InputState => {
            if (errorText) return 'error';
            if (successText) return 'success';
            if (warningText) return 'warning';
            return state;
        };

        const currentState = getState();
        const currentHelperText = getHelperText();

        return (
            <InputContainer $fullWidth={fullWidth}>
                {label && (
                    <Label $state={currentState} htmlFor={props.id}>
                        {label}
                    </Label>
                )}

                <InputWrapper
                    $variant={variant}
                    $size={size}
                    $state={currentState}
                    $focused={focused}
                    $disabled={disabled}
                >
                    {leftAddon && (
                        <AddonWrapper $position="left">
                            {leftAddon}
                        </AddonWrapper>
                    )}

                    {leftIcon && (
                        <IconWrapper $position="left">
                            {leftIcon}
                        </IconWrapper>
                    )}

                    <InputField
                        ref={ref}
                        $size={size}
                        $hasLeftIcon={!!leftIcon}
                        $hasRightIcon={!!rightIcon}
                        $hasLeftAddon={!!leftAddon}
                        $hasRightAddon={!!rightAddon}
                        disabled={disabled}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        {...props}
                    />

                    {rightIcon && (
                        <IconWrapper $position="right">
                            {rightIcon}
                        </IconWrapper>
                    )}

                    {rightAddon && (
                        <AddonWrapper $position="right">
                            {rightAddon}
                        </AddonWrapper>
                    )}
                </InputWrapper>

                {currentHelperText && (
                    <HelperText $state={currentState}>
                        {currentHelperText}
                    </HelperText>
                )}
            </InputContainer>
        );
    }
);

Input.displayName = 'Input';

// ===== 기본 내보내기 =====
export default Input;
